import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Download } from 'lucide-react';

const HeroSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 60;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(139, 38, 53, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });
      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = 'https://customer-assets.emergentagent.com/job_744874e0-0fe3-4cd2-a918-10824da88281/artifacts/7oog39co_Sperah_Full_Combined_Presentation_Final.pptx';
    link.download = 'Sperah_Project_Management_Presentation.pptx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#F5F1E8] via-white to-[#FFF8F0]">
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />
      
      {/* Floating 3D Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="floating-element absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-[#8B2635]/10 to-[#A63446]/5 rounded-3xl transform rotate-12 animate-float" style={{ animationDelay: '0s' }} />
        <div className="floating-element absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-[#D4A574]/10 to-[#E8C4A0]/5 rounded-2xl transform -rotate-12 animate-float" style={{ animationDelay: '1s' }} />
        <div className="floating-element absolute bottom-32 left-1/4 w-40 h-40 bg-gradient-to-br from-[#8B2635]/5 to-[#A63446]/10 rounded-full animate-float" style={{ animationDelay: '2s' }} />
        <div className="floating-element absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-[#D4A574]/5 to-[#E8C4A0]/10 rounded-3xl transform rotate-45 animate-float" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Logo */}
        <div className="mb-12 animate-fade-in-down">
          <h1 className="text-6xl md:text-8xl font-black mb-4 tracking-tight">
            <span className="bg-gradient-to-r from-[#8B2635] to-[#A63446] bg-clip-text text-transparent">SPERAH</span>
          </h1>
          <p className="text-2xl md:text-3xl font-semibold text-gray-700 tracking-wide">PROJECT MANAGEMENT</p>
        </div>

        {/* Tagline */}
        <div className="mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
            Excellence in Project Planning,<br />
            <span className="text-[#8B2635]">Development & Execution</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We transform complex challenges into streamlined processes, delivering exceptional results through intelligent project management methodologies.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Button
            onClick={scrollToServices}
            size="lg"
            className="bg-gradient-to-r from-[#8B2635] to-[#A63446] hover:from-[#A63446] hover:to-[#8B2635] text-white px-8 py-6 text-lg font-semibold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
          >
            Explore Our Services
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            onClick={handleDownload}
            size="lg"
            variant="outline"
            className="border-2 border-[#8B2635] text-[#8B2635] hover:bg-[#8B2635] hover:text-white px-8 py-6 text-lg font-semibold shadow-lg transform hover:scale-105 transition-all duration-300"
          >
            <Download className="mr-2 h-5 w-5" />
            Download Presentation
          </Button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#8B2635] rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-[#8B2635] rounded-full animate-scroll" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;